import i18n from '@risksmart-app/i18n/src/i18n';
import type { ControlRegisterResponse } from '@risksmart-app/trpc/src/types';
import type {
  Control_Bool_Exp,
  GetControlsQuery,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  GetControlsDocument,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import type { UseQueryOptions } from '@tanstack/react-query';
import { useMemo } from 'react';
import { mapTrpcControlsToGraphQL } from 'src/hooks/queries/control/useGetControlsRegister';
import { useGetRisksFlat } from 'src/hooks/queries/risk/useGetRisksFlat';
import { useLabelledFields as useControlLabelledFields } from 'src/pages/controls/useLabelledFields';

import { useCommonLookupLazy } from '@/hooks/useCommonLookupLazy';
import useEntityWhereFilter from '@/hooks/useEntityWhereFilter';
import { getFriendlyId } from '@/utils/friendlyId';
import type { StatefulTableOptions } from '@/utils/table/hooks/useGetStatelessTableProps';
import type { TableFields, TableRecord } from '@/utils/table/types';

import { UNRATED } from '../../gigawidget/types';
import { createDataSource } from '../createDataSource';
import { defaultClickthroughFilter } from '../dataSourceHelpers';
import { simpleField, useRelationshipTableProps } from './relationship-helpers';

export type RiskControlRow = TableRecord & {
  Id: string;
  RiskId: string;
  RiskFriendlyId: string;
  RiskTitle: string;
  RiskTier: string;
  RiskStatus: string;
  RiskTreatment: string;
  ControlId: string;
  ControlFriendlyId: string;
  ControlTitle: string;
  ControlType: string;
  DesignEffectiveness: string;
  PerformanceEffectiveness: string;
  OverallEffectiveness: string;
  TestScheduleStatus: string;
  TestScheduleStatusLabelled: string;
};

export default createDataSource({
  group: 'relationship',
  hasAccess: () => true,
  parentTypes: [Parent_Type_Enum.Risk, Parent_Type_Enum.Control],
  documentNode: GetControlsDocument,
  trpcQuery: (trpc) => {
    const queryOptions = trpc.frontend.control.register.queryOptions({
      parentId: undefined,
    });
    const transformedQueryOptions = {
      ...queryOptions,
      select: (data: ControlRegisterResponse) => mapTrpcControlsToGraphQL(data),
    };

    return transformedQueryOptions as UseQueryOptions<unknown, Error>;
  },
  useDefaultVariables: () => ({
    where: useEntityWhereFilter<Control_Bool_Exp>(Parent_Type_Enum.Control),
  }),
  useTablePropsHook: (
    data: GetControlsQuery | undefined,
    options: StatefulTableOptions<RiskControlRow>
  ) => {
    const fields = useMemo<TableFields<RiskControlRow>>(
      () => ({
        RiskFriendlyId: simpleField(i18n.t('columns.id')),
        RiskTitle: simpleField(i18n.t('risks.columns.risk_name')),
        RiskTier: simpleField(i18n.t('risks.columns.risk_tier')),
        RiskStatus: simpleField(i18n.t('risks.columns.risk_status')),
        RiskTreatment: simpleField(i18n.t('risks.columns.risk_treatment')),
        ControlFriendlyId: simpleField(i18n.t('columns.id')),
        ControlTitle: simpleField(i18n.t('controls.columns.title')),
        ControlType: simpleField(i18n.t('controls.columns.type')),
        DesignEffectiveness: simpleField(
          i18n.t('controls.columns.design_effectiveness')
        ),
        PerformanceEffectiveness: simpleField(
          i18n.t('controls.columns.performance_effectiveness')
        ),
        OverallEffectiveness: simpleField(
          i18n.t('controls.columns.effectiveness')
        ),
        TestScheduleStatusLabelled: simpleField(
          i18n.t('controls.columns.testScheduleStatus')
        ),
      }),
      []
    );

    const controlsLabelled = useControlLabelledFields(data?.control);
    const { getByValue: commonLookup } = useCommonLookupLazy();
    const { data: riskData } = useGetRisksFlat({ queryArgs: {} });

    const joinedData = useMemo<RiskControlRow[] | undefined>(() => {
      if (!controlsLabelled || !riskData?.risk) {
        return undefined;
      }

      const riskMap = new Map(riskData.risk.map((r) => [r.Id, r]));

      return controlsLabelled.flatMap((control) =>
        (control.parents ?? [])
          .filter(
            (p) => p.parent?.ObjectType === Parent_Type_Enum.Risk && p.parent.Id
          )
          .map((parent) => {
            const risk = riskMap.get(parent.parent!.Id);

            return {
              Id: `${parent.parent!.Id}_${control.Id}`,
              RiskId: parent.parent!.Id,
              RiskFriendlyId: risk?.SequentialId
                ? getFriendlyId(Parent_Type_Enum.Risk, risk.SequentialId)
                : '',
              RiskTitle: risk?.Title ?? parent.risk?.Title ?? '',
              RiskTier: commonLookup('tiers', risk?.Tier)?.label ?? '',
              RiskStatus: commonLookup('statuses', risk?.Status)?.label ?? '',
              RiskTreatment:
                commonLookup('treatments', risk?.Treatment)?.label ?? '',
              ControlId: control.Id,
              ControlFriendlyId: control.SequentialIdLabel ?? '',
              ControlTitle: control.Title ?? '',
              ControlType: control.Type ?? '',
              DesignEffectiveness: control.DesignEffectivenessLabelled ?? '',
              PerformanceEffectiveness:
                control.PerformanceEffectivenessLabelled ?? '',
              OverallEffectiveness: control.OverallEffectivenessLabelled ?? '',
              TestScheduleStatus: control.TestScheduleStatus ?? '',
              TestScheduleStatusLabelled:
                control.TestScheduleStatusLabelled ?? '',
            };
          })
      );
    }, [controlsLabelled, riskData?.risk, commonLookup]);

    return useRelationshipTableProps(
      joinedData,
      fields,
      i18n.t('risk_control_other'),
      options
    );
  },
  entityNamePlural: 'risk_control_other',
  entityNameSingular: 'risk_control_one',
  fields: 'riskControls.fields',
  dashboardFilterConfig: {},
  categoryGetters: [
    {
      id: 'riskFriendlyId',
      name: () => i18n.t('columns.id'),
      categoryGetter: (item: RiskControlRow) => item.RiskFriendlyId || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('RiskFriendlyId'),
    },
    {
      id: 'riskTitle',
      name: () => i18n.t('risks.columns.risk_name'),
      categoryGetter: (item: RiskControlRow) => item.RiskTitle || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('RiskTitle'),
    },
    {
      id: 'riskTier',
      name: () => i18n.t('risks.columns.risk_tier'),
      categoryGetter: (item: RiskControlRow) => item.RiskTier || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('RiskTier'),
    },
    {
      id: 'riskStatus',
      name: () => i18n.t('risks.columns.risk_status'),
      categoryGetter: (item: RiskControlRow) => item.RiskStatus || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('RiskStatus'),
    },
    {
      id: 'riskTreatment',
      name: () => i18n.t('risks.columns.risk_treatment'),
      categoryGetter: (item: RiskControlRow) => item.RiskTreatment || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('RiskTreatment'),
    },
    {
      id: 'controlFriendlyId',
      name: () => i18n.t('columns.id'),
      categoryGetter: (item: RiskControlRow) =>
        item.ControlFriendlyId || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('ControlFriendlyId'),
    },
    {
      id: 'controlTitle',
      name: () => i18n.t('controls.columns.title'),
      categoryGetter: (item: RiskControlRow) => item.ControlTitle || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('ControlTitle'),
    },
    {
      id: 'controlType',
      name: () => i18n.t('controls.columns.type'),
      categoryGetter: (item: RiskControlRow) => item.ControlType || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('ControlType'),
    },
    {
      id: 'designEffectiveness',
      name: () => i18n.t('controls.columns.design_effectiveness'),
      categoryGetter: (item: RiskControlRow) =>
        item.DesignEffectiveness || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('DesignEffectiveness'),
      ratingColourKey: 'design_effectiveness',
    },
    {
      id: 'performanceEffectiveness',
      name: () => i18n.t('controls.columns.performance_effectiveness'),
      categoryGetter: (item: RiskControlRow) =>
        item.PerformanceEffectiveness || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('PerformanceEffectiveness'),
      ratingColourKey: 'performance_effectiveness',
    },
    {
      id: 'overallEffectiveness',
      name: () => i18n.t('controls.columns.effectiveness'),
      categoryGetter: (item: RiskControlRow) =>
        item.OverallEffectiveness || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('OverallEffectiveness'),
      ratingColourKey: 'effectiveness',
    },
    {
      id: 'testScheduleStatus',
      name: () => i18n.t('controls.columns.testScheduleStatus'),
      categoryGetter: (item: RiskControlRow) =>
        item.TestScheduleStatus === '-'
          ? UNRATED
          : (item.TestScheduleStatusLabelled ?? UNRATED),
      clickthroughFilter: defaultClickthroughFilter(
        'TestScheduleStatusLabelled'
      ),
    },
  ],
});
